import Papa from "papaparse";
import { PeriodType } from "./period_type";

export type Demand = {
  id: string;
  sku: string;
  location: string;
  channel: string | null;
  order_placed_date: string;
  quantity: number; // int
  revenue: number | null; // Decimal | None
  runId: string;
};

export type DemandWithSkuTags = {
  [key: string]: string | null | number;
} & Demand;

export class DemandFromCsv {
  private demands: DemandWithSkuTags[];
  private setLoading: (v: boolean) => void;
  private runId: string;
  private maxRevenue: number | null = null;
  private maxQuantity: number | null = null;

  constructor(setLoading: (v: boolean) => void, runId: string) {
    this.demands = [];
    this.setLoading = setLoading;
    this.runId = runId;
  }

  resetDemand() {
    this.demands = [];
    this.maxRevenue = null;
    this.maxQuantity = null;
    this.runId = "";
  }

  setRunId(runId: string) {
    this.runId = runId;
  }

  getMinMax() {
    return {
      maxRevenue: this.maxRevenue,
      maxQuantity: this.maxQuantity,
    };
  }

  async fetch(csvUrl: string) {
    this.setLoading(true);
    this.demands = [];
    Papa.parse<Demand>(csvUrl, {
      download: true,
      worker: true,
      header: true,
      dynamicTyping: true,
      step: (results) => {
        const { sku, location, channel, order_placed_date, quantity, revenue } =
          results.data;
        this.demands.push({
          id: `${sku}${
            location ? location.toLowerCase() : location
          }${channel}${order_placed_date}`,
          sku,
          location: location ? location.toLowerCase() : "Unknown",
          channel: channel || null,
          order_placed_date: order_placed_date,
          quantity:
            quantity === null || quantity === undefined
              ? 0
              : parseInt(`${quantity}`),
          revenue:
            revenue === null || revenue === undefined
              ? null
              : parseFloat(`${revenue}`),
          runId: this.runId,
        });
      },
      complete: () => {
        this.maxRevenue = Math.max(
          ...(this.demands
            .map((d) => d.revenue)
            .filter((d) => d !== null && d !== undefined) as number[])
        );
        this.maxQuantity = Math.max(
          ...(this.demands
            .map((d) => d.quantity)
            .filter((d) => d !== null && d !== undefined) as number[])
        );
        this.setLoading(false);
      },
    });
  }

  addSkuTags(skuTagDict: Record<string, Record<string, string>>) {
    const allTagsSet = new Set<string>([]);
    Object.keys(skuTagDict).forEach((sku) => {
      Object.keys(skuTagDict[sku]).forEach((tag) => allTagsSet.add(tag));
    });
    this.demands.forEach((demand) => {
      const { sku } = demand;
      const tags = skuTagDict[sku] || {};
      [...allTagsSet.keys()].forEach((tag: string) => {
        demand[tag] = tags[tag] || "Unknown";
      });
    });
  }

  getDemand(
    minDate: Date | null,
    maxDate: Date | null,
    aggregatePeriodType: PeriodType | null
  ) {
    const modMinDate =
      minDate &&
      (aggregatePeriodType
        ? aggregatePeriodType === PeriodType.MONTH
          ? new Date(
              Date.UTC(minDate.getUTCFullYear(), minDate.getUTCMonth(), 1)
            )
          : new Date(
              Date.UTC(
                minDate.getUTCFullYear(),
                minDate.getUTCMonth(),
                minDate.getUTCDate() -
                  (minDate.getUTCDay() === 0 ? 7 : minDate.getUTCDay()) +
                  1
              )
            )
        : minDate);
    const modMaxDate = maxDate;
    return this.demands
      .filter((demand) => {
        const demand_date = new Date(`${demand.order_placed_date}Z`);
        if (modMinDate) {
          if (modMaxDate) {
            return demand_date >= modMinDate && demand_date <= modMaxDate;
          }
          return demand_date >= modMinDate;
        }
        if (modMaxDate) {
          return demand_date <= modMaxDate;
        }
        return true;
      })
      .filter(
        (demand) =>
          demand.location !== "" &&
          demand.channel !== "" &&
          demand.channel !== undefined &&
          demand.channel !== null
      );
  }
}
